import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { Play, Sparkles } from "lucide-react";
import api from "@/lib/api";

const GRAD = "linear-gradient(135deg, #8b5cf6 0%, #3b82f6 50%, #ec4899 100%)";
const H = { fontFamily: "Outfit, sans-serif" };
const IDX_KEY = "qrise_demo_idx";

function useCountUpOnView(target, inView, ms = 2000) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView || !target) return undefined;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / ms);
      setVal(Math.round(target * (1 - Math.pow(1 - p, 4))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, inView, ms]);
  return val;
}

function StatCounter({ value, label, detail, testId }) {
  const { i18n } = useTranslation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const val = useCountUpOnView(value, inView);
  const locale = i18n.language?.startsWith("fr") ? "fr-FR" : "en-US";
  return (
    <div ref={ref} className="min-w-0 text-center sm:text-left" data-testid={testId}>
      <div style={H} className="qrise-text-gradient text-3xl font-bold leading-none tabular-nums" data-testid={`${testId}-value`}>
        {val.toLocaleString(locale)}
      </div>
      <div className="mt-1.5 text-[12px] font-semibold leading-snug text-zinc-700">{label}</div>
      {detail && <div className="mt-0.5 text-[11px] text-zinc-400" data-testid={`${testId}-breakdown`}>{detail}</div>}
    </div>
  );
}

function Counters({ stats }) {
  const { t, i18n } = useTranslation();
  const n = (v) => (v || 0).toLocaleString(i18n.language?.startsWith("fr") ? "fr-FR" : "en-US");
  const both = stats.plays > 0 && stats.total > 0;
  return (
    <div className={`grid w-full gap-4 sm:divide-x sm:divide-black/[0.06] ${both ? "grid-cols-2" : "grid-cols-1"}`}>
      {stats.plays > 0 && (
        <StatCounter value={stats.plays} label={t("lp.rank.plays_label")} detail={t("lp.rank.plays_detail")} testId="plays-counter" />
      )}
      {stats.total > 0 && (
        <div className={stats.plays > 0 ? "sm:pl-4" : ""}>
          <StatCounter
            value={stats.total}
            label={t("lp.rank.counter_label")}
            detail={t("lp.rank.counter_breakdown", { reviews: n(stats.reviews), follows: n(stats.follows), likes: n(stats.likes) })}
            testId="actions-counter"
          />
        </div>
      )}
    </div>
  );
}

function DemoButton({ games }) {
  const { t, i18n } = useTranslation();
  const [idx, setIdx] = useState(() => {
    const saved = Number(localStorage.getItem(IDX_KEY));
    return Number.isInteger(saved) && saved >= 0 ? saved % games.length : Math.floor(Math.random() * games.length);
  });
  const game = games[idx];
  const suffix = i18n.language?.startsWith("en") ? "-en" : "";
  const play = () => {
    window.open(`/play/${game.slug}${suffix}`, "_blank", "noopener");
    const next = (idx + 1) % games.length;
    localStorage.setItem(IDX_KEY, String(next));
    setIdx(next);
  };
  return (
    <div className="flex flex-col items-center">
      <motion.button
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.97 }}
        onClick={play}
        style={{ ...H, background: GRAD }}
        className="group inline-flex items-center gap-2 rounded-full px-5 py-3 text-[14px] font-bold text-white shadow-[0_14px_34px_-12px_rgba(139,92,246,0.7)]"
        data-testid="hero-demo-btn"
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20 transition-transform duration-300 group-hover:scale-110">
          <Play className="h-3 w-3 fill-white" />
        </span>
        {t("lp.rank.demo_btn")}
      </motion.button>
      <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-zinc-500" data-testid="hero-demo-next">
        <Sparkles className="h-3 w-3 text-violet-500" />
        {t("lp.rank.demo_next", { game: t(`lp.game_${game.k}_name`) })}
      </div>
    </div>
  );
}

export default function HeroDemoRow({ games }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/public/stats/actions").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  const showCounter = stats?.total > 0 || stats?.plays > 0;
  return (
    <div
      className="mt-6 flex flex-col items-center gap-5 rounded-3xl border border-black/[0.06] bg-white/80 px-5 py-5 shadow-[0_10px_30px_-18px_rgba(15,23,42,0.3)] backdrop-blur-xl"
      data-testid="hero-demo-row"
    >
      {showCounter && <Counters stats={stats} />}
      {showCounter && <div className="h-px w-full bg-black/[0.05]" />}
      <DemoButton games={games} />
    </div>
  );
}
